<template>
  <div>
    <banner-component title="Création de site vitrine à Toulouse, pensé pour le référencement" :lead="lead" :breadcrumbs="breadcrumbs" ruled>
      <router-link class="btn btn--primary" to="/contact">Demander un devis</router-link>
    </banner-component>

    <section class="band" aria-labelledby="why-title">
      <div class="container columns-2">
        <div>
          <h2 id="why-title">Pourquoi un site vitrine ?</h2>
          <p class="lead">Un site vitrine est plus qu'une carte de visite en ligne : c'est l'outil de communication qui travaille pour vous quand vous ne répondez pas au téléphone.</p>
        </div>
        <ul class="ticks">
          <li><strong>Être trouvé sur Google</strong> par les clients qui cherchent ce que vous proposez, près de chez vous ou partout en France.</li>
          <li><strong>Valoriser votre activité</strong>, vos services et votre savoir-faire avec vos propres mots.</li>
          <li><strong>Gagner en crédibilité</strong> auprès de vos clients et de vos partenaires.</li>
          <li><strong>Rester accessible à toute heure</strong>, sans limite de temps ni de frontières.</li>
        </ul>
      </div>
    </section>

    <section class="band band--white" aria-labelledby="included-title">
      <div class="container">
        <h2 id="included-title">Ce que comprend la création de votre site</h2>
        <div class="grid">
          <div>
            <h3>Un design sur mesure</h3>
            <p>Votre site reflète votre identité et donne envie de vous contacter dès le premier écran. Pas de modèle repris à l'identique d'un autre site : le design est pensé pour votre activité.</p>
          </div>
          <div>
            <h3>Un référencement naturel (SEO) soigné</h3>
            <p>Structure des pages, titres, descriptions, rapidité d'affichage, plan du site, données structurées : les bases techniques sont posées dès la conception, pas ajoutées après coup.</p>
          </div>
          <div>
            <h3>Une solution clé en main</h3>
            <p>Nom de domaine, hébergement, mise en ligne et maintenance : je m'occupe de tout, pour que vous restiez concentré sur votre activité.</p>
          </div>
          <div>
            <h3>Un interlocuteur unique</h3>
            <p>De la conception à la mise en ligne, vous parlez toujours à la même personne. Une question, un besoin particulier : vous avez une réponse directe.</p>
          </div>
          <div>
            <h3>Une totale liberté</h3>
            <p>Vous souhaitez changer de prestataire plus tard ? Tous les accès et la documentation nécessaires vous sont fournis.</p>
          </div>
        </div>
      </div>
    </section>

    <section class="band" aria-labelledby="steps-title">
      <div class="container container--narrow">
        <h2 id="steps-title">Comment se déroule un projet</h2>
        <ol class="steps">
          <li><strong>Échange</strong> sur votre activité, vos clients et ce que le site doit leur apporter.</li>
          <li><strong>Devis personnalisé</strong>, avec les pages, les fonctions et le calendrier.</li>
          <li><strong>Conception et design</strong> : vous validez la direction avant le développement.</li>
          <li><strong>Développement et référencement</strong> : intégration, rédaction des balises, tests sur ordinateur et téléphone.</li>
          <li><strong>Mise en ligne</strong>, nom de domaine et hébergement compris si vous le souhaitez.</li>
          <li><strong>Maintenance</strong> et évolutions une fois le site publié.</li>
        </ol>
      </div>
    </section>

    <advices-component/>

    <section class="band" aria-labelledby="faq-title">
      <div class="container container--narrow">
        <h2 id="faq-title">Questions fréquentes</h2>
        <faq-section :items="faq"/>
      </div>
    </section>

    <call-to-action-component variant="services"/>
  </div>
</template>

<script>
  import pages from "@/content/pages";
  import {inline} from "@/seo/inline";
  import {useSeo} from "@/seo/useSeo";
  import {organization, person, webPage, breadcrumb, faqPage, service, absoluteUrl} from "@/seo/schema";
  import BannerComponent from "@/components/BannerComponent.vue";
  import FaqSection from "@/components/FaqSection.vue";
  import AdvicesComponent from "@/components/AdvicesComponent.vue";
  import CallToActionComponent from "@/components/CallToActionComponent.vue";

  const PATH = '/site-web';

  const BREADCRUMBS = [
    {name: 'Accueil', path: '/'},
    {name: 'Création de site web', path: PATH}
  ];

  const FAQ = [
    {
      q: 'Combien coûte un site vitrine ?',
      a: 'Le prix dépend du nombre de pages, des fonctions demandées et de l\'hébergement. Décrivez votre projet sur la page [contact](/contact) : vous recevez un devis personnalisé.'
    },
    {
      q: 'Dois-je m\'occuper de l\'hébergement et du nom de domaine ?',
      a: 'Non, sauf si vous le préférez. Je peux réserver le nom de domaine, héberger le site et assurer sa maintenance.'
    },
    {
      q: 'Mon site sera-t-il bien placé sur Google ?',
      a: 'Personne ne peut garantir une position. Je pose les bases techniques du référencement naturel (structure, balises, rapidité, plan du site) ; le reste dépend de la concurrence sur vos mots-clés et du temps. Un site qui publie des contenus utiles progresse plus vite.'
    },
    {
      q: 'Puis-je changer de prestataire plus tard ?',
      a: 'Oui. Tous les accès et la documentation vous sont remis, le site vous appartient.'
    }
  ];

  export default {
    name: 'ShowcaseWebsiteView',
    components: {BannerComponent, FaqSection, AdvicesComponent, CallToActionComponent},
    setup() {
      const page = pages.find(entry => entry.path === PATH);
      useSeo({
        path: PATH,
        image: '/img/og/site-web.jpg',
        graph: [
          organization(),
          person(),
          webPage(PATH, page.title, page.description, {breadcrumb: {'@id': `${absoluteUrl(PATH)}#breadcrumb`}}),
          {...breadcrumb(BREADCRUMBS), '@id': `${absoluteUrl(PATH)}#breadcrumb`},
          service('Création de site vitrine', page.description, PATH, 'Création de site web'),
          faqPage(FAQ)
        ]
      });
    },
    data: function () {
      return {
        faq: FAQ,
        breadcrumbs: BREADCRUMBS,
        lead: inline('Entrepreneur indépendant à Toulouse, je conçois des **sites vitrines sur mesure** : un design qui vous ressemble, un code rapide et un référencement naturel travaillé dès la première ligne.'),
      }
    }
  }
</script>

<style lang="stylus" scoped>
@import '~/src/common/style/constants.styl';

.columns-2
  display grid
  gap 32px
  align-items start

.ticks
  list-style none
  margin 0
  padding 0
  li
    margin 0
    padding 14px 0 14px 34px
    border-bottom 1px solid RULE
    position relative
    &::before
      content ''
      position absolute
      left 4px
      top 22px
      width 14px
      height 14px
      background LEMON
      border 2px solid INK
      border-radius 50%
  li:first-child
    border-top 2px solid INK

.grid
  display grid
  gap 28px
  margin-top 32px
  h3
    margin 0 0 8px
    padding-top 14px
    border-top 2px solid INK
    font-size 1.3rem
  p
    margin 0
    color INK_SOFT

.steps
  margin 28px 0 0
  padding 0
  list-style none
  counter-reset step
  li
    position relative
    margin 0
    padding 16px 0 16px 56px
    border-bottom 1px solid RULE
    counter-increment step
    &::before
      content counter(step)
      position absolute
      left 0
      top 14px
      width 36px
      height 36px
      display flex
      align-items center
      justify-content center
      background INK
      color LEMON
      border-radius 50%
      font-family 'Calder-LC', 'Trebuchet MS', system-ui, sans-serif
      font-size 1.1rem
  li:first-child
    border-top 2px solid INK

@media (min-width: DESKTOP_SIZE)
  .columns-2
    grid-template-columns 1fr 1fr
    gap 64px
  .grid
    grid-template-columns repeat(3, 1fr)
</style>
